// src/pages/DashboardNavigation/utils/dashboardNavigationUtils.js

import {
  DISPLAY_BY_PATH_RAW,
  PATH_LABELS,
} from "../data/dashboardNavigationData";

export const normalizePath = (path) =>
  (path || "")
    .toLowerCase()
    .replace(/\/$/, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const normalizeText = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const DISPLAY_BY_PATH = Object.fromEntries(
  Object.entries(DISPLAY_BY_PATH_RAW).map(([path, key]) => [
    normalizePath(path),
    key,
  ]),
);

export const unwrapSettings = (settings) => {
  if (!settings) return {};

  let output = settings;

  if (Array.isArray(output)) output = output[0] || {};
  if (output?.dataValues) output = output.dataValues;

  return output || {};
};

export const isTrue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["true", "1", "yes", "on"].includes(normalized);
  }

  return Boolean(value);
};

export const toRoleKey = (role) => {
  if (role === 2 || /^utilisateur|user$/i.test(String(role))) {
    return "user";
  }

  if (role === 3 || /^enseignant|teacher$/i.test(String(role))) {
    return "enseignant";
  }

  return "admin";
};

export const getPathLabel = (path) => {
  const normalized = normalizePath(path);

  const found = Object.keys(PATH_LABELS).find(
    (key) => normalizePath(key) === normalized,
  );

  return found ? PATH_LABELS[found] : "Page interne";
};

export const shouldDisplayPath = ({ path, settings }) => {
  const key = DISPLAY_BY_PATH[normalizePath(path)];

  if (!key) return true;

  return isTrue(settings[key]);
};

export const buildNavigationSections = ({
  sectionsById = {},
  sectionIds = [],
  settings = {},
}) => {
  return sectionIds
    .map((sectionId) => sectionsById[sectionId])
    .filter(Boolean)
    .map((section) => {
      const items = section.items
        .filter((path) => shouldDisplayPath({ path, settings }))
        .map((path, localIndex) => ({
          path: path.replace(/\/$/, ""),
          norm: normalizePath(path),
          label: getPathLabel(path),
          localIndex,
          sectionId: section.id,
          sectionLabel: section.label,
          groupTitle: section.label,
        }));

      return {
        ...section,
        items,
      };
    })
    .filter((section) => section.items.length > 0);
};

export const flattenNavigationSections = (sections = []) => {
  let globalIndex = 0;

  return sections.flatMap((section) =>
    section.items.map((item) => {
      const page = {
        ...item,
        index: globalIndex,
        sectionId: section.id,
        sectionLabel: section.label,
        groupTitle: section.label,
      };

      globalIndex += 1;

      return page;
    }),
  );
};

export const getNearbyPages = (pages = [], currentIndex, radius = 3) => {
  if (!pages.length) return [];

  const index = currentIndex >= 0 ? currentIndex : 0;
  const wantedSize = radius * 2 + 1;

  let start = Math.max(0, index - radius);
  let end = Math.min(pages.length, index + radius + 1);

  if (end - start < wantedSize) {
    if (start === 0) {
      end = Math.min(pages.length, wantedSize);
    } else if (end === pages.length) {
      start = Math.max(0, pages.length - wantedSize);
    }
  }

  return pages.slice(start, end).map((page) => {
    const distance = page.index - index;

    return {
      ...page,
      distance,
      isCurrent: distance === 0 && currentIndex !== -1,
      isPrevious: distance === -1 && currentIndex !== -1,
      isNext: distance === 1 && currentIndex !== -1,
    };
  });
};

export const getNavigationProgress = ({ currentIndex, total }) => {
  if (!total) return 100;
  if (currentIndex < 0) return 0;
  if (total === 1) return 100;

  return ((currentIndex + 1) / total) * 100;
};